import {
  BoltTaskStatuses,
  BoltTaskTypePriorities,
  IBoltTaskSLA,
  IBoltTaskSOP,
  IBoltTaskTemplate,
  IPerson,
  SLAUnits,
} from "@bolt/core";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import {
  ComplexField,
  ComplexRendererBuilder,
  EnumRadioField,
  EnumSingleSelectFieldV2,
  EnumSingleSelectFieldV2Props,
  NumericField,
  NumericFieldProps,
  PrimaryCard,
  SchemaFactoryV2,
  TextField,
  TextFieldProps,
  ComplexFieldProps,
  RichTextEditorFieldProps,
  RichTextEditorField,
  RichTextEditorValueType,
  utils,
  AsyncODataDropdownField,
} from "@ucl/library";
import { get } from "lodash";
import { SLAComponent } from "../../SlaComponent/SlaComponent";
import {
  SOPComponent,
  SOPComponentProps,
} from "../../SOPComponent/SOPComponent";
import { defaultTaskTemplate } from "../CreateTaskTemplateForm";
import {
  taskPriorityOptions,
  optionalTaskStatus,
} from "./CreateBoltTaskTemplateFormContainer";
import { RichTextEditorsOutputTextTypes } from "../../../types/globalTypes";

export function multiStepTaskTypeForm(
  factory: SchemaFactoryV2,
  _apiBaseUrl: string,
  assigneeOdataUrl: string,
  taskTemplates: IBoltTaskTemplate[],
  updateTaskTemplate: (taskTemplate: IBoltTaskTemplate[]) => void,
  getUserBearerTokenRequestHeader: () => Promise<
    Record<string, string> | undefined
  >,
  richTextEditorsOutputTextType?: RichTextEditorsOutputTextTypes,
  _errors?: { [key: string]: string[] }
) {
  const parentTaskTemplate = taskTemplates[0];
  const taskTemplateSchema = get(factory.schema?.schema, "BoltTaskTemplate");
  const richTextEditorOutputTypeIsPlainText =
    !utils.miscellaneous.isNullOrUndefined(richTextEditorsOutputTextType) &&
    richTextEditorsOutputTextType === RichTextEditorsOutputTextTypes.PLAIN;

  return (
    <>
      <TooltipComponent
        content={`Name of the Parent Task Template. You will identify workflow steps for this task template below.  This Task Template will be saved in the queue selected in Workflow Step 1.`}
        position="BottomLeft"
        target=".task-type-name .bp5-form-content"
      >
        <div className="task-type-name">
          <TextField
            {...(get(
              taskTemplateSchema,
              "BoltTaskTemplate__TaskTemplateName"
            ) as TextFieldProps)}
            placeholder="Type here..."
            value={parentTaskTemplate.taskTemplateName}
            onSubmit={(value) => {
              updateTaskTemplate(
                taskTemplates.map((tt) =>
                  tt.orderNumber === 0
                    ? { ...tt, taskTemplateName: value as string }
                    : tt
                )
              );
            }}
            isRequired
          />
        </div>
      </TooltipComponent>
      <RichTextEditorField
        {...(get(
          taskTemplateSchema,
          "BoltTaskTemplate__TaskTemplateDescription"
        ) as RichTextEditorFieldProps)}
        placeholder="Type here to add a description..."
        value={parentTaskTemplate.taskTemplateDescription}
        valueType={RichTextEditorValueType.html}
        richTextEditorSettings={{
          toolbarSettings: {
            enable: richTextEditorOutputTypeIsPlainText ? false : true,
          },
        }}
        getPlainTextOnSubmit={(value) => {
          if (richTextEditorOutputTypeIsPlainText) {
            updateTaskTemplate(
              taskTemplates.map((tt) =>
                tt.orderNumber === 0
                  ? { ...tt, taskTemplateDescription: value as string }
                  : tt
              )
            );
          }
        }}
        onSubmit={(value) => {
          if (!richTextEditorOutputTypeIsPlainText) {
            updateTaskTemplate(
              taskTemplates.map((tt) =>
                tt.orderNumber === 0
                  ? { ...tt, taskTemplateDescription: value as string }
                  : tt
              )
            );
          }
        }}
        isRequired
      />
      {taskTemplates.map((tt, i) => {
        const isParentTask = tt.orderNumber === 0;
        const showOnHolidays = tt.initialStatus != BoltTaskStatuses.OnHold;

        return (
          <>
            <PrimaryCard
              key={`workflow-step-${tt.orderNumber + 1}`}
              headerContent={
                taskTemplates.length > 1 ? (
                  <div className="ucl-card-header">
                    <h4 className="bp5-heading">{`Workflow Step ${i + 1}`}</h4>
                    <div className="workflow-header-actions-container">
                      {!isParentTask && (
                        <>
                          <TooltipComponent
                            content={
                              "This workflow step will be created when the previous step is COMPLETED."
                            }
                            target="label.bp5-radio:first-of-type"
                            position="BottomCenter"
                          >
                            <TooltipComponent
                              content={
                                "This workflow step will be created when the previous step is CREATED."
                              }
                              target="label.bp5-radio:last-of-type"
                              position="BottomCenter"
                            >
                              <EnumRadioField
                                type="EnumRadio"
                                fieldName="isTriggeredSequentially"
                                fieldKey="isTriggeredSequentially"
                                optionValues={[
                                  { label: "Triggered Sequentially", value: 1 },
                                  { label: "Trigger In Parallel", value: 2 },
                                ]}
                                value={tt.isTriggeredSequentially ? 1 : 2}
                                onSubmit={function (value) {
                                  updateTaskTemplate(
                                    taskTemplates.map((ogTT) =>
                                      ogTT.orderNumber === tt.orderNumber
                                        ? {
                                            ...ogTT,
                                            isTriggeredSequentially: value == 1,
                                          }
                                        : ogTT
                                    )
                                  );
                                }}
                                isRequired
                              />
                            </TooltipComponent>
                          </TooltipComponent>
                          <TooltipComponent
                            content={"Deletes this workflow step only."}
                            position="BottomCenter"
                            target=".workflow-header-actions-delete"
                          >
                            <div
                              className="workflow-header-actions-delete"
                              onClick={() => {
                                updateTaskTemplate(
                                  taskTemplates.filter(
                                    (ogTT) => ogTT.orderNumber != tt.orderNumber
                                  )
                                );
                              }}
                            >
                              <FontAwesomeIcon
                                className="app-header-breadcrumb-chevron"
                                icon={faTrashCan}
                              />
                            </div>
                          </TooltipComponent>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  `Workflow Step ${i + 1}`
                )
              }
            >
              <div className="task-type-form-flex-wrapper">
                <TextField
                  {...(get(
                    taskTemplateSchema,
                    "BoltTaskTemplate__StepName"
                  ) as TextFieldProps)}
                  placeholder="Type here..."
                  isRequired={true}
                  value={tt.stepName}
                  onSubmit={(value) => {
                    updateTaskTemplate(
                      taskTemplates.map((ogTT) =>
                        ogTT.orderNumber === tt.orderNumber
                          ? { ...ogTT, stepName: value }
                          : ogTT
                      )
                    );
                  }}
                />
              </div>
              <div className="task-type-form-flex-wrapper-2">
                <AsyncODataDropdownField
                  type="AsyncODataDropdown"
                  label="Assignee"
                  odataUrl={assigneeOdataUrl}
                  getAuthRequestHeader={getUserBearerTokenRequestHeader}
                  value={tt.assigneeId}
                  textColumnName={"fullName"}
                  valueColumnName={"id"}
                  groupByColumnName="type"
                  renderProps={{
                    showHeader: false,
                  }}
                  onSubmit={(value) => {
                    const state = value as unknown as IPerson;
                    const assigneeId = (state as unknown as IPerson)?.id ?? "";

                    updateTaskTemplate(
                      taskTemplates.map((ogTT) =>
                        ogTT.orderNumber === tt.orderNumber
                          ? { ...ogTT, assigneeId }
                          : ogTT
                      )
                    );
                  }}
                />
                <div className="task-type-form--spacer"></div>
                <SLAComponent
                  {...(get(
                    taskTemplateSchema,
                    "BoltTaskTemplate__SLA_AsObject"
                  ) as ComplexFieldProps<IBoltTaskSLA>)}
                  value={{ sla: tt.sla, slaUnit: tt.slaUnit }}
                  availableSLAUnits={[SLAUnits.Day, SLAUnits.Hour]}
                  onSubmit={function (value: IBoltTaskSLA) {
                    updateTaskTemplate(
                      taskTemplates.map((ogTT) =>
                        ogTT.orderNumber === tt.orderNumber
                          ? {
                              ...ogTT,
                              sla: value.sla,
                              slaUnit: value.slaUnit,
                            }
                          : ogTT
                      )
                    );
                  }}
                  isRequired
                />
                <div className="task-type-form--spacer"></div>
                <SLAComponent
                  {...(get(
                    taskTemplateSchema,
                    "BoltTaskTemplate__ExpectedSLA_AsObject"
                  ) as ComplexFieldProps<IBoltTaskSLA>)}
                  value={{ sla: tt.expectedSLA, slaUnit: tt.expectedSLAUnit }}
                  availableSLAUnits={[SLAUnits.Minute]}
                  onSubmit={function (value: IBoltTaskSLA) {
                    updateTaskTemplate(
                      taskTemplates.map((ogTT) =>
                        ogTT.orderNumber === tt.orderNumber
                          ? {
                              ...ogTT,
                              expectedSLA: value.sla,
                              expectedSLAUnit: SLAUnits.Minute,
                            }
                          : ogTT
                      )
                    );
                  }}
                />
              </div>
              <ComplexField
                {...(get(
                  taskTemplateSchema,
                  "BoltTaskTemplate__SOP_AsObject"
                ) as ComplexFieldProps<IBoltTaskSOP>)}
                value={{
                  sop: tt.taskTemplateSOP,
                  sop_AsList: tt.taskTemplateSOP_AsList,
                }}
                renderer={
                  SOPComponent as ComplexRendererBuilder<SOPComponentProps>
                }
                onSubmit={(value: IBoltTaskSOP) => {
                  updateTaskTemplate(
                    taskTemplates.map((ogTT) =>
                      ogTT.orderNumber === tt.orderNumber
                        ? {
                            ...ogTT,
                            boltTaskTypeSOP_AsList: value.sop_AsList || [],
                          }
                        : ogTT
                    )
                  );
                }}
              />
              <RichTextEditorField
                {...(get(
                  taskTemplateSchema,
                  "BoltTaskTemplate__ShortDescription"
                ) as RichTextEditorFieldProps)}
                placeholder="Type here..."
                value={tt.shortDescription}
                valueType={RichTextEditorValueType.html}
                richTextEditorSettings={{
                  toolbarSettings: {
                    enable: richTextEditorOutputTypeIsPlainText ? false : true,
                  },
                }}
                getPlainTextOnSubmit={(value) => {
                  if (richTextEditorOutputTypeIsPlainText) {
                    updateTaskTemplate(
                      taskTemplates.map((ogTT) =>
                        ogTT.orderNumber === tt.orderNumber
                          ? {
                              ...ogTT,
                              shortDescription: value as string,
                            }
                          : ogTT
                      )
                    );
                  }
                }}
                onSubmit={(value) => {
                  if (!richTextEditorOutputTypeIsPlainText) {
                    updateTaskTemplate(
                      taskTemplates.map((ogTT) =>
                        ogTT.orderNumber === tt.orderNumber
                          ? {
                              ...ogTT,
                              shortDescription: value as string,
                            }
                          : ogTT
                      )
                    );
                  }
                }}
                isRequired
              />
              <div className="task-type-form-flex-wrapper-2">
                <EnumSingleSelectFieldV2
                  {...(get(
                    taskTemplateSchema,
                    "BoltTaskTemplate__InitialStatus"
                  ) as EnumSingleSelectFieldV2Props)}
                  value={tt.initialStatus}
                  optionValues={optionalTaskStatus.filter(
                    (o) => o.value != 2 && o.value != 4
                  )}
                  isRequired={false}
                  onSubmit={(value) => {
                    updateTaskTemplate(
                      taskTemplates.map((ogTT) =>
                        ogTT.orderNumber === tt.orderNumber
                          ? {
                              ...ogTT,
                              initialStatus: value as BoltTaskStatuses,
                            }
                          : ogTT
                      )
                    );
                  }}
                />
                <div
                  className="task-type-onholddays"
                  hidden={tt.initialStatus != BoltTaskStatuses.OnHold}
                >
                  <NumericField
                    {...(get(
                      taskTemplateSchema,
                      "BoltTaskTemplate__OnHoldDays"
                    ) as NumericFieldProps)}
                    placeholder="0"
                    format="n0"
                    showIncrementButtons={false}
                    value={tt.onHoldDays}
                    onSubmit={(value) => {
                      updateTaskTemplate(
                        taskTemplates.map((ogTT) =>
                          ogTT.orderNumber === tt.orderNumber
                            ? {
                                ...ogTT,
                                onHoldDays: value as number,
                              }
                            : ogTT
                        )
                      );
                    }}
                  />
                </div>
                <div
                  className={showOnHolidays ? "task-type-priority-field" : ""}
                >
                  <EnumSingleSelectFieldV2
                    {...(get(
                      taskTemplateSchema,
                      "BoltTaskTemplate__TaskPriority"
                    ) as EnumSingleSelectFieldV2Props)}
                    value={tt.taskPriority}
                    isRequired
                    optionValues={taskPriorityOptions}
                    onSubmit={(value) => {
                      updateTaskTemplate(
                        taskTemplates.map((ogTT) =>
                          ogTT.orderNumber === tt.orderNumber
                            ? {
                                ...ogTT,
                                taskPriority: value as BoltTaskTypePriorities,
                              }
                            : ogTT
                        )
                      );
                    }}
                  />
                </div>
              </div>
            </PrimaryCard>
            {tt.orderNumber === taskTemplates.length - 1 ? (
              <button
                className="button-reset task-type-multi-step-button"
                onClick={() => {
                  updateTaskTemplate(
                    taskTemplates.concat({
                      ...defaultTaskTemplate,
                      taskTemplateName: parentTaskTemplate.taskTemplateName,
                      taskTemplateDescription:
                        parentTaskTemplate.taskTemplateDescription,
                      orderNumber: tt.orderNumber + 1,
                      hasMultiSteps: true,
                    })
                  );
                }}
              >
                ADD WORKFLOW STEP
              </button>
            ) : null}
          </>
        );
      })}
    </>
  );
}
